import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MarkdownField } from "@/components/MarkdownField";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, errText } from "@/lib/api";
import { shortDate } from "@/lib/eventMeta";
import { useAuth } from "@/context/AuthContext";

const blank = { title: "", case_number: "", client_name: "", court_id: null, stage: "", status: "active", notes: "" };

export default function Matters() {
  const { canWrite, workspace, role } = useAuth();
  const navigate = useNavigate();
  const [matters, setMatters] = useState([]);
  const [courts, setCourts] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);

  const load = useCallback(async () => {
    if (role === "clerk") return;
    try {
      const [m, c] = await Promise.all([
        api.get("/matters", { params: q ? { q } : {} }),
        api.get("/courts"),
      ]);
      setMatters(m.data);
      setCourts(c.data);
    } catch (e) {
      toast.error(errText(e));
    }
  }, [q, workspace?.workspace_id, role]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Matter title is required");
    try {
      await api.post("/matters", {
        ...form,
        title: form.title.trim(),
        case_number: form.case_number || null,
        client_name: form.client_name || null,
        stage: form.stage || null,
        notes: form.notes || null,
      });
      toast.success("Matter created");
      setForm(blank);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const courtName = (id) => courts.find((c) => c.court_id === id)?.name || "—";

  return (
    <div className="space-y-8" data-testid="matters-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="label-caps">Case diary</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mt-2">Matters</h1>
        </div>
        {canWrite ? (
          <Button
            data-testid="new-matter-btn"
            onClick={() => setOpen(true)}
            className="rounded-full h-12 px-6 text-base"
          >
            <Plus className="h-5 w-5 mr-2" /> New matter
          </Button>
        ) : null}
      </div>

      <div className="relative max-w-md">
        <Search className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="matters-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search title, case number or client"
          className="pl-11 rounded-sm h-12 text-base"
        />
      </div>

      <div className="surface overflow-x-auto" data-testid="matters-table">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-border">
              {["Matter", "Case no.", "Client", "Court", "Next date", "Status"].map((h) => (
                <th key={h} className="px-5 py-4 label-caps whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matters.map((m) => (
              <tr
                key={m.matter_id}
                data-testid={`matter-row-${m.matter_id}`}
                onClick={() => navigate(`/matters/${m.matter_id}`)}
                className="border-b border-border cursor-pointer hover:bg-accent/50 transition-colors duration-200"
              >
                <td className="px-5 py-4 text-base font-semibold">{m.title}</td>
                <td className="px-5 py-4 text-sm">{m.case_number || "—"}</td>
                <td className="px-5 py-4 text-sm">{m.client_name || "—"}</td>
                <td className="px-5 py-4 text-sm">{courtName(m.court_id)}</td>
                <td className="px-5 py-4 text-sm" data-testid={`matter-next-date-${m.matter_id}`}>
                  {m.next_hearing_date ? (
                    <span className="chip chip-session">{shortDate(m.next_hearing_date)}</span>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-5 py-4">
                  <span className="chip chip-session">{m.status}</span>
                </td>
              </tr>
            ))}
            {matters.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-5 py-10 text-center text-muted-foreground">
                  No matters yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-xl max-h-[75vh] overflow-y-auto" data-testid="matter-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">New matter</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="label-caps">Title</Label>
              <Input
                data-testid="matter-title-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Sharma v. State of Delhi"
                className="rounded-sm h-12 text-base"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-caps">Case number</Label>
                <Input
                  data-testid="matter-case-number-input"
                  value={form.case_number}
                  onChange={(e) => setForm({ ...form, case_number: e.target.value })}
                  placeholder="CRL.A. 512/2026"
                  className="rounded-sm h-12 text-base"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Client</Label>
                <Input
                  data-testid="matter-client-input"
                  value={form.client_name}
                  onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                  className="rounded-sm h-12 text-base"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-caps">Court</Label>
                <Select
                  value={form.court_id || "none"}
                  onValueChange={(v) => setForm({ ...form, court_id: v === "none" ? null : v })}
                >
                  <SelectTrigger data-testid="matter-court-select" className="rounded-sm h-11 text-base">
                    <SelectValue placeholder="Select court" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No court</SelectItem>
                    {courts.map((c) => (
                      <SelectItem key={c.court_id} value={c.court_id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-caps">Stage</Label>
                <Input
                  data-testid="matter-stage-input"
                  value={form.stage}
                  onChange={(e) => setForm({ ...form, stage: e.target.value })}
                  placeholder="Final arguments"
                  className="rounded-sm h-12 text-base"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Notes</Label>
              <MarkdownField
                value={form.notes || ""}
                onChange={(v) => setForm({ ...form, notes: v })}
                testId="matter-notes-input"
                minHeight={90}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full h-11" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button data-testid="matter-save-btn" onClick={save} className="rounded-full h-11 px-6">
              Create matter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
